import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'

import { useMount } from 'utils/hooks'

import {
  ALL_RISK_LEVEL_VALUE,
  GET_CAMPAIGNS_LIST_ASYNC,
  GET_LEVEL_COUNTS_ASYNC,
  SET_PLACEHOLDER_FILTER,
  SET_RISK_LEVEL_SELECTED_FILTERS,
} from 'containers/SpearPhishingAgent/constants'
import {
  selectCampaignsData,
  selectCampaignsTotalCount,
  selectRiskLevelSelectedFilters,
} from 'containers/SpearPhishingAgent/selectors'

import { trackPsimaRemediationFilter } from '../../psimaPendo'

import CardSection from './components/CardSection'
import FiltersSection from './components/FiltersSection'
import ImpactPreview from './components/ImpactPreview'
import QuickLaunchModal from './components/QuickLaunchModal'
import SmartFilter from './components/SmartFilter'
import TopBanner, {
  TOP_BANNER_PLACEHOLDER_API_FILTERS,
  TOP_BANNER_PLACEHOLDER_FILTERS,
} from './components/TopBanner'
import { clearDashboardDeepLinkParams, parseDashboardDeepLink } from './deepLinkParams'

const MainTitle = styled.div`
  font-size: var(--t-typography-font-size-lg);
  color: var(--color-text);
  font-weight: var(--t-typography-font-weight-medium);
  margin-bottom: 20px;
`

/** @typedef {'idle' | 'awaiting-filtered' | 'awaiting-fallback' | 'done'} DeepLinkPhase */

const hydrateDeepLinkFilter = (dispatch, filterId) => {
  dispatch({
    type: SET_PLACEHOLDER_FILTER,
    payload: {
      value: TOP_BANNER_PLACEHOLDER_API_FILTERS[filterId] || null,
      skipFetch: true,
    },
  })
}

const Dashboard = () => {
  const dispatch = useDispatch()
  const riskLevelSelectedFilters = useSelector(selectRiskLevelSelectedFilters)
  const campaignsTotalCount = useSelector(selectCampaignsTotalCount)
  const campaignsData = useSelector(selectCampaignsData)
  const [isQuickLaunchModalOpen, setIsQuickLaunchModalOpen] = useState(false)
  const [activePlaceholderId, setActivePlaceholderId] = useState(null)

  // Capture deep-link intent once — survives URL cleanup and StrictMode remounts.
  const deepLinkRef = useRef(parseDashboardDeepLink())
  const phaseRef = useRef(
    /** @type {DeepLinkPhase} */ (deepLinkRef.current ? 'awaiting-filtered' : 'idle')
  )
  const [isResolvingDeepLink, setIsResolvingDeepLink] = useState(
    () => phaseRef.current === 'awaiting-filtered'
  )

  useLayoutEffect(() => {
    const deepLink = deepLinkRef.current
    if (!deepLink || phaseRef.current === 'done' || phaseRef.current === 'idle') {
      return
    }

    // Keep filter hydrated for the first cards fetch (and across StrictMode remount).
    hydrateDeepLinkFilter(dispatch, deepLink.filter)

    if (!deepLink.clearIfEmpty && phaseRef.current === 'awaiting-filtered') {
      setActivePlaceholderId(deepLink.filter)
      trackPsimaRemediationFilter({ filter: deepLink.filter })
    }
  }, [dispatch])

  useMount(() => {
    dispatch(GET_LEVEL_COUNTS_ASYNC.initiate())

    return () => {
      const phase = phaseRef.current
      // During deep-link resolve, do not cancel cards or re-touch filter state —
      // StrictMode cleanup was undoing clearIfEmpty and aborting the fallback fetch.
      if (phase === 'awaiting-filtered' || phase === 'awaiting-fallback') {
        dispatch(GET_LEVEL_COUNTS_ASYNC.cancel())
        return
      }

      dispatch(GET_CAMPAIGNS_LIST_ASYNC.cancel())
      dispatch(GET_LEVEL_COUNTS_ASYNC.cancel())
      dispatch({
        type: SET_RISK_LEVEL_SELECTED_FILTERS,
        payload: { value: [ALL_RISK_LEVEL_VALUE], skipFetch: true },
      })
      dispatch({
        type: SET_PLACEHOLDER_FILTER,
        payload: { value: null, skipFetch: true },
      })
    }
  })

  useEffect(() => {
    const phase = phaseRef.current
    if (phase === 'idle' || phase === 'done' || campaignsData.loader) {
      return
    }

    const deepLink = deepLinkRef.current
    if (!deepLink) {
      phaseRef.current = 'done'
      setIsResolvingDeepLink(false)
      return
    }

    const hasCards = campaignsData.items.length > 0

    if (phase === 'awaiting-filtered') {
      // Ignore the initial idle render (loader=false, filter not hydrated yet).
      // Otherwise clearIfEmpty fires before the filtered request even starts.
      if (!campaignsData.placeholderFilter) {
        return
      }

      if (deepLink.clearIfEmpty && !hasCards) {
        phaseRef.current = 'awaiting-fallback'
        dispatch({
          type: SET_PLACEHOLDER_FILTER,
          payload: { value: null },
        })
        return
      }

      if (deepLink.clearIfEmpty && hasCards) {
        setActivePlaceholderId(deepLink.filter)
        trackPsimaRemediationFilter({ filter: deepLink.filter })
      }

      phaseRef.current = 'done'
      clearDashboardDeepLinkParams()
      deepLinkRef.current = null
      setIsResolvingDeepLink(false)
      return
    }

    if (phase === 'awaiting-fallback') {
      phaseRef.current = 'done'
      clearDashboardDeepLinkParams()
      deepLinkRef.current = null
      setIsResolvingDeepLink(false)
    }
  }, [campaignsData.loader, campaignsData.items.length, campaignsData.placeholderFilter, dispatch])

  const activeFilter = activePlaceholderId
    ? TOP_BANNER_PLACEHOLDER_FILTERS[activePlaceholderId]
    : null

  const handlePlaceholderClick = (id) => {
    if (activePlaceholderId === id) {
      return
    }

    setActivePlaceholderId(id)

    const isAllRiskLevels =
      !riskLevelSelectedFilters.length || riskLevelSelectedFilters.includes(ALL_RISK_LEVEL_VALUE)

    if (!isAllRiskLevels) {
      dispatch({
        type: SET_RISK_LEVEL_SELECTED_FILTERS,
        payload: { value: [ALL_RISK_LEVEL_VALUE] },
      })
    }

    trackPsimaRemediationFilter({ filter: id })

    dispatch({
      type: SET_PLACEHOLDER_FILTER,
      payload: { value: TOP_BANNER_PLACEHOLDER_API_FILTERS[id] || null },
    })
  }

  const handleClearPlaceholder = () => {
    if (!activePlaceholderId) {
      return
    }

    setActivePlaceholderId(null)
    dispatch({
      type: SET_PLACEHOLDER_FILTER,
      payload: { value: null },
    })
  }

  return (
    <>
      <TopBanner
        activePlaceholderId={activePlaceholderId}
        onPlaceholderClick={handlePlaceholderClick}
      />
      <MainTitle>Suggested Simulations</MainTitle>
      <SmartFilter
        isVisible={Boolean(activePlaceholderId) && !isResolvingDeepLink}
        label={activeFilter?.label}
        resultsLabel={`${campaignsTotalCount} ${campaignsTotalCount === 1 ? 'result' : 'results'}`}
        onClear={handleClearPlaceholder}
      />
      <FiltersSection />
      <CardSection
        setIsQuickLaunchModalOpen={setIsQuickLaunchModalOpen}
        forceLoader={isResolvingDeepLink}
      />
      <ImpactPreview setIsQuickLaunchModalOpen={setIsQuickLaunchModalOpen} />
      <QuickLaunchModal
        isQuickLaunchModalOpen={isQuickLaunchModalOpen}
        setIsQuickLaunchModalOpen={setIsQuickLaunchModalOpen}
      />
    </>
  )
}

export default Dashboard
